import React from 'react';
import { useTranslation } from 'react-i18next';

const COLORS = {
  bg: '#0a0a0f', surface: '#12121a', border: '#1e1e2e',
  text: '#e8e8ed', textDim: '#8888a0', textMuted: '#55556a',
  logos: '#6366F1', gold: '#F59E0B', viability: '#10B981',
  entropy: '#EF4444', coherence: '#06B6D4', accent: '#A855F7',
};
const fonts = { mono: "'JetBrains Mono', 'SF Mono', monospace", sans: "'Inter', sans-serif", display: "'Sora', sans-serif" };

/**
 * LEARNING MOMENT TRIGGERS:
 * 1. levin      — First Levin signal detected
 * 2. vCritical  — V falls below 0.30
 * 3. pause      — First PAUSA verdict
 * 4. lambdaJump — Λ improves 0.10+ in one session
 * 5. hoffman    — First Hoffman bias detected
 * 6. emergence  — ERS > 0.70 for the first time
 * 7. aMinus     — P(A⁻) > 10% in Monte Carlo
 */

const TRIGGER_ICONS = {
  levin: '⚡', vCritical: '⛽', pause: '🚦', lambdaJump: '🚀',
  hoffman: '🪞', emergence: '✨', aMinus: '⚠️',
};

const LS_KEY = 'logos-learning-moments';

function getSeenMoments() {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}');
  } catch { return {}; }
}

function markSeen(triggerId) {
  try {
    const seen = getSeenMoments();
    seen[triggerId] = Date.now();
    localStorage.setItem(LS_KEY, JSON.stringify(seen));
  } catch {}
}

export function checkLearningTrigger({ signals, V, verdict, deltaLambda, biases, ers, pAMinus }) {
  const seen = getSeenMoments();
  if (signals?.length > 0 && !seen.levin) return 'levin';
  if (V < 0.30 && !seen.vCritical) return 'vCritical';
  if (verdict === 'PAUSA' && !seen.pause) return 'pause';
  if (deltaLambda > 0.10 && !seen.lambdaJump) return 'lambdaJump';
  if (biases?.length > 0 && !seen.hoffman) return 'hoffman';
  if (ers > 0.70 && !seen.emergence) return 'emergence';
  if (pAMinus > 0.10 && !seen.aMinus) return 'aMinus';
  return null;
}

export default function LearningMoment({ triggerId, onClose }) {
  const { t } = useTranslation();
  if (!triggerId) return null;

  const icon = TRIGGER_ICONS[triggerId] || '💡';
  const title = t(`learningMoments.${triggerId}.title`, triggerId);
  const body = t(`learningMoments.${triggerId}.body`, '');
  const action = t(`learningMoments.${triggerId}.action`, '');

  const handleClose = () => {
    markSeen(triggerId);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeInUp 0.3s ease',
    }}>
      <div style={{
        width: '88%', maxWidth: 420,
        background: `linear-gradient(145deg, ${COLORS.surface}, ${COLORS.bg})`,
        border: `1px solid ${COLORS.logos}30`, borderRadius: 16,
        padding: '28px 24px', position: 'relative',
        boxShadow: `0 0 60px ${COLORS.logos}15`,
      }}>
        {/* Icon */}
        <div style={{ textAlign: 'center', fontSize: 48, marginBottom: 12 }}>
          {icon}
        </div>

        {/* Title */}
        <h3 style={{
          textAlign: 'center', fontSize: 17, fontWeight: 700, margin: '0 0 16px 0',
          fontFamily: fonts.display, color: COLORS.text,
          background: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.logos})`,
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </h3>

        {/* Body */}
        <div style={{
          fontSize: 13, lineHeight: 1.65, color: COLORS.textDim,
          fontFamily: fonts.sans, whiteSpace: 'pre-line', marginBottom: 16,
        }}>
          {body}
        </div>

        {/* Action tip */}
        {action && (
          <div style={{
            padding: '12px 14px', background: `${COLORS.viability}10`,
            border: `1px solid ${COLORS.viability}25`, borderRadius: 10,
            marginBottom: 20,
          }}>
            <div style={{
              fontSize: 11, lineHeight: 1.5, color: COLORS.viability,
              fontFamily: fonts.sans,
            }}>
              💡 {action}
            </div>
          </div>
        )}

        {/* Close button */}
        <button onClick={handleClose} style={{
          width: '100%', padding: '12px 20px', borderRadius: 10, cursor: 'pointer',
          background: `linear-gradient(135deg, ${COLORS.logos}80, ${COLORS.logos})`,
          border: 'none', color: '#fff', fontFamily: fonts.mono, fontSize: 12,
          fontWeight: 600, letterSpacing: 1.5,
          boxShadow: `0 4px 20px ${COLORS.logos}40`,
        }}>
          {t('learningMoments.understood', 'Entendido ✦')}
        </button>
      </div>
    </div>
  );
}
